import { createFileLogger } from '../../../utils/logger';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import {
  Document, Paragraph, TextRun, HeadingLevel, Packer, AlignmentType, UnderlineType,
  ExternalHyperlink, Table, TableRow, TableCell, WidthType, ShadingType, BorderStyle,
  ImageRun,
} from 'docx';
import { customMarkdownSerializer } from './documentSerializers';
import { API_ORIGIN_URL } from '../../../api/config';

const log = createFileLogger('EXPORT_UTILS');

/** Subset of doc_style Y.Map values relevant to export styling. */
export interface DocStyleSnapshot {
  fontFamily?: string;
  h1FontSize?: number;
  h2FontSize?: number;
  h3FontSize?: number;
  h4FontSize?: number;
  h5FontSize?: number;
  h6FontSize?: number;
  h1Color?: string;
  h2Color?: string;
  h3Color?: string;
  h4Color?: string;
  h5Color?: string;
  h6Color?: string;
  pageBackgroundStyle?: string;
  pageColor?: string;
  // Page margins (CSS length strings, e.g. "1in", "72pt")
  pageMarginTop?: string;
  pageMarginRight?: string;
  pageMarginBottom?: string;
  pageMarginLeft?: string;
}

export interface ExportToWordOptions {
  documentTitle: string;
  prosemirrorDoc?: ProseMirrorNode;
  docStyles?: DocStyleSnapshot;
}

export interface ExportToPDFOptions {
  documentTitle: string;
  prosemirrorDoc?: any;
  docStyles?: DocStyleSnapshot;
}

export interface ExportToMarkdownOptions {
  documentTitle: string;
  editorState?: any;
}

export interface ExportToPlainTextOptions {
  documentTitle: string;
  editorState?: any;
}

const sanitizeFilename = (title: string): string =>
  title.replace(/[/\\:*?"<>|]/g, '').trim() || 'Document';

const triggerDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// ─── HTML serialization (for PDF print) ─────────────────────────────────────

const escapeHtml = (str: string): string => {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const nodeToHtml = (node: any): string => {
  if (!node) return '';

  switch (node.type) {
    case 'paragraph': {
      const content = node.content ? node.content.map(nodeToHtml).join('') : '';
      const styles: string[] = [];
      if (node.attrs?.textAlign && node.attrs.textAlign !== 'left') styles.push(`text-align:${node.attrs.textAlign}`);
      if (node.attrs?.lineHeight) styles.push(`line-height:${node.attrs.lineHeight}`);
      if (node.attrs?.indent) styles.push(`padding-left:${node.attrs.indent * 36}pt`);
      const styleAttr = styles.length > 0 ? ` style="${styles.join(';')}"` : '';
      return `<p${styleAttr}>${content || '<br>'}</p>`;
    }
    case 'heading': {
      const level = node.attrs?.level || 1;
      const content = node.content ? node.content.map(nodeToHtml).join('') : '';
      const styles: string[] = [];
      if (node.attrs?.textAlign && node.attrs.textAlign !== 'left') styles.push(`text-align:${node.attrs.textAlign}`);
      if (node.attrs?.lineHeight) styles.push(`line-height:${node.attrs.lineHeight}`);
      if (node.attrs?.indent) styles.push(`padding-left:${node.attrs.indent * 36}pt`);
      const styleAttr = styles.length > 0 ? ` style="${styles.join(';')}"` : '';
      return `<h${level}${styleAttr}>${content}</h${level}>`;
    }
    case 'blockquote': {
      const content = node.content ? node.content.map(nodeToHtml).join('') : '';
      return `<blockquote>${content}</blockquote>`;
    }
    case 'code_block': {
      const text = node.content ? node.content.map((c: any) => c.text || '').join('') : '';
      const lang = node.attrs?.language || '';
      return `<pre><code class="${lang}">${escapeHtml(text)}</code></pre>`;
    }
    case 'bullet_list': {
      const items = node.content ? node.content.map(nodeToHtml).join('') : '';
      return `<ul>${items}</ul>`;
    }
    case 'ordered_list': {
      const items = node.content ? node.content.map(nodeToHtml).join('') : '';
      const start = node.attrs?.order > 1 ? ` start="${node.attrs.order}"` : '';
      return `<ol${start}>${items}</ol>`;
    }
    case 'list_item': {
      const content = node.content ? node.content.map(nodeToHtml).join('') : '';
      return `<li>${content}</li>`;
    }
    case 'horizontal_rule':
      return '<hr>';
    case 'page_break':
      // In HTML/print context, a page break is rendered as a CSS page-break
      return '<div style="page-break-after: always; break-after: page; height: 1px; margin: 16px 0;"></div>';
    case 'footnote': {
      const num = node.attrs?.number || 1;
      return `<sup style="color:#3b82f6;font-size:0.7em;font-weight:600;" title="${escapeHtml(node.attrs?.content || '')}">[${num}]</sup>`;
    }
    case 'hard_break':
      return '<br>';
    case 'table': {
      const rows = node.content ? node.content.map(nodeToHtml).join('') : '';
      return `<table>${rows}</table>`;
    }
    case 'table_row': {
      const cells = node.content ? node.content.map(nodeToHtml).join('') : '';
      return `<tr>${cells}</tr>`;
    }
    case 'table_cell': {
      const content = node.content ? node.content.map(nodeToHtml).join('') : '';
      const bg = node.attrs?.background || node.attrs?.backgroundColor || '';
      const align = node.attrs?.textAlign || 'left';
      const colspan = node.attrs?.colspan > 1 ? ` colspan="${node.attrs.colspan}"` : '';
      const rowspan = node.attrs?.rowspan > 1 ? ` rowspan="${node.attrs.rowspan}"` : '';
      const style = `text-align:${align};${bg ? `background-color:${bg};` : ''}`;
      return `<td${colspan}${rowspan} style="${style}">${content}</td>`;
    }
    case 'table_header': {
      const content = node.content ? node.content.map(nodeToHtml).join('') : '';
      const bg = node.attrs?.background || node.attrs?.backgroundColor || '';
      const align = node.attrs?.textAlign || 'left';
      const colspan = node.attrs?.colspan > 1 ? ` colspan="${node.attrs.colspan}"` : '';
      const rowspan = node.attrs?.rowspan > 1 ? ` rowspan="${node.attrs.rowspan}"` : '';
      const style = `text-align:${align};${bg ? `background-color:${bg};` : ''}`;
      return `<th${colspan}${rowspan} style="${style}">${content}</th>`;
    }
    case 'text': {
      let text = escapeHtml(node.text || '');
      if (!node.marks) return text;

      let linkMark: any = null;
      const styleProps: string[] = [];

      node.marks.forEach((mark: any) => {
        switch (mark.type) {
          case 'strong':      text = `<strong>${text}</strong>`; break;
          case 'em':          text = `<em>${text}</em>`; break;
          case 'underline':   text = `<u>${text}</u>`; break;
          case 'strike':      text = `<s>${text}</s>`; break;
          case 'code':        text = `<code>${text}</code>`; break;
          case 'subscript':   text = `<sub>${text}</sub>`; break;
          case 'superscript': text = `<sup>${text}</sup>`; break;
          case 'highlight':
            styleProps.push('background-color:#FFFF00');
            break;
          case 'smallCaps':
            styleProps.push('font-variant:small-caps');
            break;
          case 'letterSpacing':
            if (mark.attrs?.value != null) styleProps.push(`letter-spacing:${mark.attrs.value}em`);
            break;
          case 'textTransform':
            if (mark.attrs?.transform) styleProps.push(`text-transform:${mark.attrs.transform}`);
            break;
          case 'fontSize':
            if (mark.attrs?.size) styleProps.push(`font-size:${mark.attrs.size}pt`);
            break;
          case 'fontFamily':
            if (mark.attrs?.font) styleProps.push(`font-family:'${mark.attrs.font}'`);
            break;
          case 'fontWeight':
            if (mark.attrs?.weight) styleProps.push(`font-weight:${mark.attrs.weight}`);
            break;
          case 'textColor':
            if (mark.attrs?.color) styleProps.push(`color:${mark.attrs.color}`);
            break;
          case 'backgroundColor':
            if (mark.attrs?.color) styleProps.push(`background-color:${mark.attrs.color}`);
            break;
          case 'link':
            linkMark = mark;
            break;
          // Strip track-changes and metadata marks — they must not appear in the exported file
          case 'deletion':
            // Deleted text should not appear in the export at all
            text = '';
            break;
          case 'insertion':
          case 'comment':
          case 'ai_detected':
            break;
        }
      });

      if (!text) return '';
      if (styleProps.length > 0) {
        text = `<span style="${styleProps.join(';')}">${text}</span>`;
      }
      if (linkMark) {
        text = `<a href="${escapeHtml(linkMark.attrs?.href || '')}">${text}</a>`;
      }
      return text;
    }
    case 'image': {
      const src = node.attrs?.src || '';
      const alt = escapeHtml(node.attrs?.alt || '');
      const width = node.attrs?.width ? ` width="${node.attrs.width}"` : '';
      const align = node.attrs?.alignment || 'left';
      const style = align === 'center' ? 'display:block;margin:0 auto;' : align === 'right' ? 'float:right;' : '';
      return `<figure style="margin:16px 0;"><img src="${escapeHtml(src)}" alt="${alt}"${width} style="max-width:100%;${style}" /></figure>`;
    }
    case 'math_inline': {
      const latex = escapeHtml(node.attrs?.latex || node.attrs?.content || '');
      return `<code class="math-inline">\\(${latex}\\)</code>`;
    }
    case 'math_display': {
      const latex = escapeHtml(node.attrs?.latex || node.attrs?.content || '');
      return `<div class="math-display" style="text-align:center;margin:16px 0;">\\[${latex}\\]</div>`;
    }
    default:
      return node.content ? node.content.map(nodeToHtml).join('') : '';
  }
};

// ─── DOCX serialization ──────────────────────────────────────────────────────

const PX_TO_EMU = 9525; // 1 CSS pixel at 96 DPI = 9525 EMU
const MAX_IMAGE_WIDTH_PX = 500;

/**
 * Fetch a URL for DOCX embedding, routing cross-origin URLs through the backend proxy
 * to avoid CORS restrictions when fetching S3 or CDN images from the browser.
 */
const fetchForDocx = async (src: string): Promise<Response> => {
  const isCrossOrigin = src.startsWith('http://') || src.startsWith('https://');
  if (!isCrossOrigin) {
    return fetch(src);
  }

  // Presigned S3 URLs already contain AWS credentials — use the unauthenticated proxy.
  if (src.includes('AWSAccessKeyId=') && src.includes('Signature=')) {
    const proxyUrl = `${API_ORIGIN_URL}/image-proxy/fetch-public?url=${encodeURIComponent(src)}`;
    return fetch(proxyUrl);
  }

  // All other cross-origin URLs go through the authenticated proxy.
  const token = localStorage.getItem('authToken');
  const proxyUrl = `${API_ORIGIN_URL}/image-proxy/fetch?url=${encodeURIComponent(src)}`;
  return fetch(proxyUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

const fetchImageForDocx = async (
  src: string,
  storedWidth: number | null,
  storedHeight: number | null,
): Promise<{ data: ArrayBuffer; width: number; height: number } | null> => {
  try {
    const response = await fetchForDocx(src);
    if (!response.ok) return null;
    const data = await response.arrayBuffer();

    // Use stored dimensions if available, otherwise detect from the image element
    let w = storedWidth || 0;
    let h = storedHeight || 0;

    if (!w || !h) {
      const dims = await new Promise<{ w: number; h: number }>((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve({ w: 400, h: 300 });
        img.src = src;
      });
      w = dims.w || 400;
      h = dims.h || 300;
    }

    // Scale down to fit MAX_IMAGE_WIDTH_PX, preserving aspect ratio
    if (w > MAX_IMAGE_WIDTH_PX) {
      h = Math.round((h * MAX_IMAGE_WIDTH_PX) / w);
      w = MAX_IMAGE_WIDTH_PX;
    }

    return { data, width: w * PX_TO_EMU, height: h * PX_TO_EMU };
  } catch {
    return null;
  }
};

/**
 * Build an array of TextRun / ExternalHyperlink from a ProseMirror inline content node.
 */
const buildTextRuns = (contentNode: ProseMirrorNode): any[] => {
  const runs: any[] = [];

  contentNode.forEach((node) => {
    // Hard break → line break in TextRun
    if (node.type.name === 'hard_break') {
      runs.push(new TextRun({ break: 1 }));
      return;
    }

    // Inline atoms (footnote, math_inline) → plain-text representation
    if (!node.isText) {
      if (node.type.name === 'footnote') {
        runs.push(new TextRun({ text: `[${node.attrs?.number ?? ''}]`, superScript: true }));
      } else if (node.type.name === 'math_inline') {
        runs.push(new TextRun({ text: node.attrs?.latex || '', font: { name: 'Cambria Math' } }));
      }
      return;
    }

    const text = node.text || '';
    const marks = node.marks || [];
    const runProps: any = { text };
    let linkMark: any = null;

    marks.forEach((mark) => {
      switch (mark.type.name) {
        case 'strong':      runProps.bold = true; break;
        case 'em':          runProps.italics = true; break;
        case 'underline':   runProps.underline = { type: UnderlineType.SINGLE }; break;
        case 'strike':      runProps.strike = true; break;
        case 'code':        runProps.font = { name: 'Courier New' }; break;
        case 'subscript':   runProps.subScript = true; break;
        case 'superscript': runProps.superScript = true; break;
        case 'smallCaps':   runProps.smallCaps = true; break;
        case 'textColor': {
          const color = (mark.attrs?.color || '').replace('#', '');
          if (/^[0-9a-fA-F]{6}$/.test(color)) runProps.color = color;
          break;
        }
        case 'backgroundColor': {
          const color = (mark.attrs?.color || '').replace('#', '');
          if (/^[0-9a-fA-F]{6}$/.test(color)) runProps.shading = { fill: color, type: ShadingType.CLEAR, color: 'auto' };
          break;
        }
        case 'highlight':
          runProps.shading = { fill: 'FFFF00', type: ShadingType.CLEAR, color: 'auto' };
          break;
        case 'fontWeight': {
          const w = parseInt(mark.attrs?.weight || '400', 10);
          if (w >= 600) runProps.bold = true;
          break;
        }
        case 'fontSize':
          if (mark.attrs?.size) runProps.size = Math.round(parseFloat(mark.attrs.size) * 2);
          break;
        case 'fontFamily':
          if (mark.attrs?.font) runProps.font = { name: mark.attrs.font };
          break;
        case 'link':
          linkMark = mark;
          break;
        // Ignore metadata/tracking marks in the exported file
        case 'deletion':
        case 'insertion':
        case 'comment':
        case 'ai_detected':
          break;
      }
    });

    if (linkMark) {
      runs.push(new ExternalHyperlink({
        link: linkMark.attrs?.href || '',
        children: [new TextRun({ ...runProps, style: 'Hyperlink' })],
      }));
    } else {
      runs.push(new TextRun(runProps));
    }
  });

  return runs.length > 0 ? runs : [new TextRun('')];
};

/**
 * Recursively build DOCX Paragraph nodes from a list node (handles nesting).
 */
const buildListParagraphs = (listNode: ProseMirrorNode, isOrdered: boolean, level = 0): any[] => {
  const result: any[] = [];

  listNode.forEach((listItem) => {
    listItem.forEach((itemChild) => {
      if (itemChild.type.name === 'paragraph') {
        const runs = buildTextRuns(itemChild);
        result.push(
          isOrdered
            ? new Paragraph({ children: runs, numbering: { reference: 'default-numbering', level } })
            : new Paragraph({ children: runs, bullet: { level } })
        );
      } else if (itemChild.type.name === 'bullet_list') {
        result.push(...buildListParagraphs(itemChild, false, level + 1));
      } else if (itemChild.type.name === 'ordered_list') {
        result.push(...buildListParagraphs(itemChild, true, level + 1));
      }
      // Other block types inside list items (code blocks, etc.) are serialized as plain text
      else {
        const text = itemChild.textContent;
        if (text) {
          result.push(
            isOrdered
              ? new Paragraph({ children: [new TextRun(text)], numbering: { reference: 'default-numbering', level } })
              : new Paragraph({ children: [new TextRun(text)], bullet: { level } })
          );
        }
      }
    });
  });

  return result;
};

/**
 * Build block-level DOCX content from any ProseMirror block node.
 * Used recursively (e.g. inside blockquotes) and for top-level document content.
 */
const buildBlockContent = async (blockNode: ProseMirrorNode, indentLeft = 0): Promise<any[]> => {
  const content: any[] = [];

  const headingLevelMap: Record<number, typeof HeadingLevel[keyof typeof HeadingLevel]> = {
    1: HeadingLevel.HEADING_1, 2: HeadingLevel.HEADING_2, 3: HeadingLevel.HEADING_3,
    4: HeadingLevel.HEADING_4, 5: HeadingLevel.HEADING_5, 6: HeadingLevel.HEADING_6,
  };

  // Build paragraph formatting options merging block-level textAlign, lineHeight, indent attrs
  const paragraphOpts = (child: ProseMirrorNode, extra: object = {}) => {
    const opts: any = { ...extra };

    // Cumulative indent: blockquote indentLeft (in twips) + paragraph-level indent attribute
    // Each paragraph indent level = 36pt = 720 twips
    const paragraphIndentTwips = (child.attrs?.indent ?? 0) * 720;
    const totalIndent = indentLeft + paragraphIndentTwips;
    if (totalIndent > 0) opts.indent = { left: totalIndent };

    // Text alignment
    const align = child.attrs?.textAlign;
    if (align === 'center') opts.alignment = AlignmentType.CENTER;
    else if (align === 'right') opts.alignment = AlignmentType.RIGHT;
    else if (align === 'justify') opts.alignment = AlignmentType.BOTH;

    // Line height — ProseMirror stores as a unitless multiplier (e.g. 1.5, 2)
    // DOCX uses line spacing in 240ths of a line where 240 = single spacing
    if (child.attrs?.lineHeight) {
      const multiplier = parseFloat(child.attrs.lineHeight);
      if (!isNaN(multiplier) && multiplier > 0) {
        opts.spacing = { line: Math.round(multiplier * 240) };
      }
    }

    return opts;
  };

  const pushes = [] as Array<() => Promise<void>>;

  blockNode.forEach((child) => {
    pushes.push(async () => {
      switch (child.type.name) {
        case 'paragraph': {
          content.push(new Paragraph({ children: buildTextRuns(child), ...paragraphOpts(child) }));
          break;
        }

        case 'heading': {
          const level = child.attrs?.level || 1;
          content.push(new Paragraph({
            heading: headingLevelMap[level] ?? HeadingLevel.HEADING_1,
            children: buildTextRuns(child),
            ...paragraphOpts(child),
          }));
          break;
        }

        case 'blockquote': {
          // Recurse with increased indent (720 twips = 0.5 inch per level)
          const inner = await buildBlockContent(child, indentLeft + 720);
          content.push(...inner);
          break;
        }

        case 'code_block': {
          // Split code text on newlines; each line becomes a TextRun with break after it
          const rawText = child.textContent || '';
          const lines = rawText.split('\n');
          const runs: any[] = [];
          lines.forEach((line, i) => {
            runs.push(new TextRun({ text: line, font: { name: 'Courier New' } }));
            if (i < lines.length - 1) runs.push(new TextRun({ break: 1 }));
          });
          content.push(new Paragraph({
            children: runs.length > 0 ? runs : [new TextRun('')],
            shading: { fill: 'F5F5F5', type: ShadingType.CLEAR, color: 'auto' },
            ...(indentLeft > 0 ? { indent: { left: indentLeft } } : {}),
          }));
          break;
        }

        case 'bullet_list':
          content.push(...buildListParagraphs(child, false, 0));
          break;

        case 'ordered_list':
          content.push(...buildListParagraphs(child, true, 0));
          break;

        case 'horizontal_rule':
          content.push(new Paragraph({
            children: [new TextRun('')],
            border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: '000000', space: 1 } },
          }));
          break;

        case 'page_break':
          content.push(new Paragraph({ children: [new TextRun({ break: 1 })], pageBreakBefore: true }));
          break;

        case 'image': {
          const { src, alt, width, height } = child.attrs || {};
          if (src) {
            const imageData = await fetchImageForDocx(src, width, height);
            if (imageData) {
              content.push(new Paragraph({
                children: [new ImageRun({ type: 'png', data: imageData.data, transformation: { width: imageData.width, height: imageData.height } })],
              }));
            } else {
              // Fallback: text placeholder when the image cannot be fetched
              const label = alt ? `[Image: ${alt}]` : '[Image]';
              content.push(new Paragraph({ children: [new TextRun({ text: label, italics: true })] }));
            }
          }
          break;
        }

        case 'math_display': {
          const latex = child.attrs?.latex || '';
          content.push(new Paragraph({
            children: [new TextRun({ text: latex, font: { name: 'Cambria Math' } })],
            ...paragraphOpts(child),
          }));
          break;
        }

        case 'table': {
          const rows: any[] = [];
          child.forEach((rowNode) => {
            if (rowNode.type.name !== 'table_row') return;
            const cells: any[] = [];
            rowNode.forEach((cellNode) => {
              const isHeader = cellNode.type.name === 'table_header';
              const cellParagraphs: any[] = [];
              cellNode.forEach((cellChild) => {
                if (cellChild.type.name === 'paragraph') {
                  cellParagraphs.push(new Paragraph({ children: buildTextRuns(cellChild) }));
                } else {
                  // Other block content in cells: serialize as plain text
                  const text = cellChild.textContent;
                  if (text) cellParagraphs.push(new Paragraph({ children: [new TextRun(text)] }));
                }
              });
              cells.push(new TableCell({
                children: cellParagraphs.length > 0 ? cellParagraphs : [new Paragraph({ children: [new TextRun('')] })],
                shading: isHeader ? { fill: 'F0F0F0', type: ShadingType.CLEAR, color: 'auto' } : undefined,
              }));
            });
            rows.push(new TableRow({ children: cells }));
          });
          if (rows.length > 0) {
            content.push(new Table({ rows, width: { size: 100, type: WidthType.PERCENTAGE } }));
          }
          break;
        }

        default: {
          const text = child.textContent;
          if (text) content.push(new Paragraph({ children: [new TextRun(text)] }));
          break;
        }
      }
    });
  });

  for (const push of pushes) await push();
  return content;
};

/**
 * Convert the top-level ProseMirror document node into DOCX content blocks.
 */
const pmDocToDocxContent = async (pmNode: ProseMirrorNode): Promise<any[]> => {
  return buildBlockContent(pmNode);
};

/**
 * Convert a CSS length string (e.g. "1in", "72pt", "2.54cm") to DOCX twips.
 * Falls back to 1440 (= 1 inch) for unknown/unparseable values.
 */
const cssLengthToTwips = (value: string | undefined, fallback = 1440): number => {
  if (!value) return fallback;
  const num = parseFloat(value);
  if (isNaN(num)) return fallback;
  if (value.endsWith('in'))  return Math.round(num * 1440);
  if (value.endsWith('pt'))  return Math.round(num * 20);       // 1 pt = 20 twips
  if (value.endsWith('pc'))  return Math.round(num * 240);      // 1 pica = 12pt = 240 twips
  if (value.endsWith('cm'))  return Math.round(num * 566.929);  // 1 cm = 566.929 twips
  if (value.endsWith('mm'))  return Math.round(num * 56.6929);  // 1 mm = 56.6929 twips
  if (value.endsWith('px'))  return Math.round(num * 15);       // 1 CSS px at 96 dpi ≈ 15 twips
  return fallback;
};

// ─── Public export functions ─────────────────────────────────────────────────

export const exportToWord = async ({ documentTitle, prosemirrorDoc, docStyles }: ExportToWordOptions): Promise<void> => {
  try {
    log.log('[ExportUtils] Starting Word export');

    if (!(prosemirrorDoc instanceof ProseMirrorNode)) {
      throw new Error('No content to export. Please ensure the document is loaded.');
    }

    const docContent = await pmDocToDocxContent(prosemirrorDoc);

    // Build DOCX paragraph/heading styles from docStyles
    const ds = docStyles ?? {};
    const hasHeadingStyles = ds.h1Color || ds.h2Color || ds.h3Color ||
      ds.h4Color || ds.h5Color || ds.h6Color ||
      ds.h1FontSize || ds.h2FontSize || ds.h3FontSize ||
      ds.h4FontSize || ds.h5FontSize || ds.h6FontSize;

    // Helper to build a run props object, filtering undefined/empty entries
    const headingRun = (color: string | undefined, size: number | undefined) =>
      Object.fromEntries(Object.entries({
        color: color ? color.replace('#', '') : undefined,
        size: size ? size * 2 : undefined, // DOCX uses half-points
        font: ds.fontFamily ? { name: ds.fontFamily } : undefined,
      }).filter(([, v]) => v !== undefined && v !== ''));

    // Correct Word built-in style names: "Heading 1" .. "Heading 6" (capital H, space)
    const headingStyleDefs = hasHeadingStyles ? [
      { id: 'Heading1', name: 'Heading 1', basedOn: 'Normal', next: 'Normal', run: headingRun(ds.h1Color, ds.h1FontSize) },
      { id: 'Heading2', name: 'Heading 2', basedOn: 'Normal', next: 'Normal', run: headingRun(ds.h2Color, ds.h2FontSize) },
      { id: 'Heading3', name: 'Heading 3', basedOn: 'Normal', next: 'Normal', run: headingRun(ds.h3Color, ds.h3FontSize) },
      { id: 'Heading4', name: 'Heading 4', basedOn: 'Normal', next: 'Normal', run: headingRun(ds.h4Color, ds.h4FontSize) },
      { id: 'Heading5', name: 'Heading 5', basedOn: 'Normal', next: 'Normal', run: headingRun(ds.h5Color, ds.h5FontSize) },
      { id: 'Heading6', name: 'Heading 6', basedOn: 'Normal', next: 'Normal', run: headingRun(ds.h6Color, ds.h6FontSize) },
    ] : [];

    // Default paragraph style — carries font family and body font size
    const defaultStyleDefs = ds.fontFamily ? [
      {
        id: 'Normal',
        name: 'Normal',
        run: { font: { name: ds.fontFamily } },
      },
    ] : [];

    const allStyles = [...defaultStyleDefs, ...headingStyleDefs];

    // Page margins from doc_style (CSS length → DOCX twips, default 1in = 1440 twips)
    const marginTop    = cssLengthToTwips(ds.pageMarginTop);
    const marginRight  = cssLengthToTwips(ds.pageMarginRight);
    const marginBottom = cssLengthToTwips(ds.pageMarginBottom);
    const marginLeft   = cssLengthToTwips(ds.pageMarginLeft);

    const wordDocument = new Document({
      ...(allStyles.length > 0 ? { styles: { paragraphStyles: allStyles as any } } : {}),
      sections: [{
        properties: {
          page: {
            margin: { top: marginTop, right: marginRight, bottom: marginBottom, left: marginLeft },
          },
        },
        children: docContent,
      }],
      numbering: {
        config: [{
          reference: 'default-numbering',
          levels: Array.from({ length: 9 }, (_, level) => ({
            level,
            format: 'decimal',
            text: `%${level + 1}.`,
            alignment: AlignmentType.LEFT,
          })),
        }],
      },
    });

    const blob = await Packer.toBlob(wordDocument);
    triggerDownload(blob, `${sanitizeFilename(documentTitle)}.docx`);

    log.log('[ExportUtils] Word export complete');
  } catch (error) {
    log.error('[ExportUtils] Error exporting to Word:', error);
    throw error;
  }
};

export const exportToPDF = async ({ documentTitle, prosemirrorDoc, docStyles }: ExportToPDFOptions): Promise<void> => {
  const cleanTitle = sanitizeFilename(documentTitle);

  const htmlContent = prosemirrorDoc?.content
    ? prosemirrorDoc.content.map((node: any) => nodeToHtml(node)).join('')
    : '<p>No content available</p>';

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    throw new Error('Could not open print window. Please allow popups for this site.');
  }

  // Build theme-aware CSS from docStyles
  const ds = docStyles ?? {};
  // Escape single quotes in the font name to prevent breaking out of the CSS string.
  const safeFontName = (ds.fontFamily ?? '').replace(/'/g, "\\'");
  const fontFamily = safeFontName ? `'${safeFontName}', sans-serif` : 'Calibri, Arial, sans-serif';
  const h1Size  = ds.h1FontSize ? `${ds.h1FontSize}pt`  : '24pt';
  const h2Size  = ds.h2FontSize ? `${ds.h2FontSize}pt`  : '18pt';
  const h3Size  = ds.h3FontSize ? `${ds.h3FontSize}pt`  : '14pt';
  const h4Size  = ds.h4FontSize ? `${ds.h4FontSize}pt`  : '12pt';
  const h5Size  = ds.h5FontSize ? `${ds.h5FontSize}pt`  : '11pt';
  const h6Size  = ds.h6FontSize ? `${ds.h6FontSize}pt`  : '11pt';
  const h1Color = ds.h1Color ?? 'inherit';
  const h2Color = ds.h2Color ?? 'inherit';
  const h3Color = ds.h3Color ?? 'inherit';
  const h4Color = ds.h4Color ?? 'inherit';
  const h5Color = ds.h5Color ?? 'inherit';
  const h6Color = ds.h6Color ?? 'inherit';

  // Page margins: use doc_style values if present, otherwise fall back to 1in.
  // Strip non-CSS-length characters to prevent injection into the @page rule.
  const safeCssLength = (v: string | undefined, fallback: string) =>
    (v ?? fallback).replace(/[^0-9a-zA-Z.%]/g, '') || fallback;
  const marginTop    = safeCssLength(ds.pageMarginTop,    '1in');
  const marginRight  = safeCssLength(ds.pageMarginRight,  '1in');
  const marginBottom = safeCssLength(ds.pageMarginBottom, '1in');
  const marginLeft   = safeCssLength(ds.pageMarginLeft,   '1in');

  // Page background: combines pageColor (solid) and pageBackgroundStyle (CSS string).
  // Both are applied as inline styles on <body> to avoid CSS injection via the style block.
  const bodyStyleParts: string[] = [];
  if (ds.pageColor && ds.pageColor !== '#ffffff' && ds.pageColor !== '#fff') {
    bodyStyleParts.push(`background-color:${ds.pageColor.replace(/"/g, '&quot;')}`);
  }
  if (ds.pageBackgroundStyle) {
    bodyStyleParts.push(ds.pageBackgroundStyle.replace(/"/g, '&quot;'));
  }
  const bodyStyle = bodyStyleParts.join(';');

  // Body font size is not part of doc_set_theme (which controls headings only); hardcode 11pt.
  const bodyFontSize = '11pt';

  printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${cleanTitle}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: ${fontFamily}; font-size: ${bodyFontSize}; line-height: 1.5; color: #000; padding: 0; }
    h1 { font-size: ${h1Size}; font-weight: bold; margin: 16pt 0 8pt; color: ${h1Color}; }
    h2 { font-size: ${h2Size}; font-weight: bold; margin: 14pt 0 8pt; color: ${h2Color}; }
    h3 { font-size: ${h3Size}; font-weight: bold; margin: 12pt 0 6pt; color: ${h3Color}; }
    h4 { font-size: ${h4Size}; font-weight: bold; margin: 10pt 0 5pt; color: ${h4Color}; }
    h5 { font-size: ${h5Size}; font-weight: bold; margin: 10pt 0 5pt; color: ${h5Color}; }
    h6 { font-size: ${h6Size}; font-weight: bold; margin: 10pt 0 5pt; color: ${h6Color}; }
    p { margin: 0 0 8pt; }
    ul, ol { margin: 0 0 8pt; padding-left: 24pt; }
    li { margin-bottom: 3pt; }
    blockquote { border-left: 3pt solid #ccc; padding-left: 12pt; margin: 8pt 0; color: #555; font-style: italic; }
    pre { background: #f5f5f5; border: 1pt solid #ddd; border-radius: 3pt; padding: 8pt; margin: 8pt 0; font-family: 'Courier New', monospace; font-size: 9pt; white-space: pre-wrap; word-break: break-word; }
    code { font-family: 'Courier New', monospace; background: #f5f5f5; padding: 1pt 3pt; border-radius: 2pt; font-size: 9pt; }
    table { border-collapse: collapse; width: 100%; margin: 8pt 0; }
    th, td { border: 1pt solid #000; padding: 4pt 8pt; text-align: left; vertical-align: top; }
    th { background-color: #f0f0f0; font-weight: bold; }
    hr { border: none; border-top: 1pt solid #000; margin: 12pt 0; }
    a { color: #1155CC; text-decoration: underline; }
    img { max-width: 100%; height: auto; display: block; margin: 8pt 0; }
    figure { margin: 8pt 0; }
    @page { margin: ${marginTop} ${marginRight} ${marginBottom} ${marginLeft}; }
  </style>
</head>
<body${bodyStyle ? ` style="${bodyStyle}"` : ''}>${htmlContent}</body>
</html>`);

  // Register onload before document.close() to avoid the race where close() fires
  // the load event synchronously (inline document with no external resources).
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
  printWindow.addEventListener('afterprint', () => printWindow.close());

  printWindow.document.close();
};

export const exportToMarkdown = async ({ documentTitle, editorState }: ExportToMarkdownOptions): Promise<void> => {
  try {
    if (!editorState?.doc) {
      throw new Error('No document content available for Markdown export.');
    }

    const markdown = customMarkdownSerializer.serialize(editorState.doc);
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    triggerDownload(blob, `${sanitizeFilename(documentTitle)}.md`);

    log.log('[ExportUtils] Markdown export complete');
  } catch (error) {
    log.error('[ExportUtils] Error exporting to Markdown:', error);
    throw error;
  }
};

export const exportToPlainText = async ({ documentTitle, editorState }: ExportToPlainTextOptions): Promise<void> => {
  try {
    if (!editorState?.doc) {
      throw new Error('No document content available for plain text export.');
    }

    const text = editorState.doc.textBetween(0, editorState.doc.content.size, '\n\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    triggerDownload(blob, `${sanitizeFilename(documentTitle)}.txt`);

    log.log('[ExportUtils] Plain text export complete');
  } catch (error) {
    log.error('[ExportUtils] Error exporting to plain text:', error);
    throw error;
  }
};
